import { z } from "zod";
import { shapeIdSchema } from "./card-id";

/** tldraw arrow routing: arc (curve via bend) or elbow (right angles). */
export const agentArrowKindSchema = z
	.enum(["arc", "elbow"])
	.describe(
		"arc = curved (use bend); elbow = orthogonal routing (use elbowMidPoint)",
	);

/**
 * Arc curvature in page units (perpendicular distance from midpoint).
 * 0 = straight. Typical |bend| 40–120. Opposite signs separate parallel arrows.
 */
export const agentArrowBendSchema = z
	.number()
	.describe(
		"Arc bend in page units; 0 straight, ±40..120curve; use opposite signs for overlapping arrows",
	);

/** Elbow midpoint along the path (0–1). */
export const agentArrowElbowMidPointSchema = z
	.number()
	.describe("Elbow mid handle position along the route (0–1)");

export const agentArrowCurveFields = {
	kind: agentArrowKindSchema.optional(),
	bend: agentArrowBendSchema.optional(),
	elbowMidPoint: agentArrowElbowMidPointSchema.optional(),
};

export const createArrowInputSchema = z
	.object({
		fromShapeId: shapeIdSchema
			.optional()
			.describe("Exact shapeId from listShapesInViewport for the arrow start"),
		toShapeId: shapeIdSchema
			.optional()
			.describe("Exact shapeId from listShapesInViewport for the arrow end"),
		x1: z.number().optional(),
		y1: z.number().optional(),
		x2: z.number().optional(),
		y2: z.number().optional(),
		label: z.string().optional().describe("Label rendered on the arrow"),
		...agentArrowCurveFields,
	})
	.superRefine((input, ctx) => {
		const hasFromShape = Boolean(input.fromShapeId);
		const hasToShape = Boolean(input.toShapeId);
		const hasStartCoords = input.x1 !== undefined && input.y1 !== undefined;
		const hasEndCoords = input.x2 !== undefined && input.y2 !== undefined;

		if (hasFromShape && hasToShape) {
			return;
		}

		if (hasFromShape && hasEndCoords) {
			return;
		}

		if (hasToShape && hasStartCoords) {
			return;
		}

		if (!hasFromShape && !hasToShape && hasStartCoords && hasEndCoords) {
			return;
		}

		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message:
				"Provide fromShapeId and toShapeId to connect shapes, or x1/y1 and x2/y2 for a free arrow",
		});
	});

export const updateArrowInputSchema = z.object({
	shapeId: shapeIdSchema,
	label: z.string().optional(),
	fromShapeId: shapeIdSchema.optional(),
	toShapeId: shapeIdSchema.optional(),
	...agentArrowCurveFields,
});

export type AgentArrowKind = z.infer<typeof agentArrowKindSchema>;
export type CreateArrowInput = z.infer<typeof createArrowInputSchema>;
export type UpdateArrowInput = z.infer<typeof updateArrowInputSchema>;
